import { useState, useEffect, useCallback } from 'react';
import { Download, X, Smartphone } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

/**
 * Detects iOS including iPadOS 13+ which reports as Mac in the user agent.
 */
function isIOSDevice(): boolean {
  const ua = navigator.userAgent;
  // Classic iOS detection
  if (/iPad|iPhone|iPod/.test(ua) && !(window as any).MSStream) return true;
  // iPadOS 13+ detection — reports as Mac but has touch
  if (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1) return true;
  return false;
}

function isIOSSafari(): boolean {
  if (!isIOSDevice()) return false;
  const ua = navigator.userAgent;
  // Safari on iOS: has "Safari" but not "CriOS" (Chrome) or "FxiOS" (Firefox) or "EdgiOS" (Edge)
  return /Safari/.test(ua) && !/CriOS|FxiOS|EdgiOS/.test(ua);
}

/**
 * Store the deferred prompt globally so the footer "Install" button can trigger it.
 */
let globalDeferredPrompt: BeforeInstallPromptEvent | null = null;

/** Let external components (e.g. footer) trigger the install prompt */
export function triggerInstall(): boolean {
  if (globalDeferredPrompt) {
    globalDeferredPrompt.prompt();
    return true;
  }
  // On iOS, clear the dismissed state and reload will show the instructions
  if (isIOSDevice()) {
    localStorage.removeItem('pwa-install-dismissed');
    // Dispatch a custom event the component listens for
    window.dispatchEvent(new CustomEvent('pwa-show-ios-prompt'));
    return true;
  }
  return false;
}

/** Check if the app is already installed as a PWA */
export function isInstalledPWA(): boolean {
  return window.matchMedia('(display-mode: standalone)').matches || !!(window.navigator as any).standalone;
}

/**
 * Smart PWA install prompt.
 * - On Android/Chrome: uses the native beforeinstallprompt event
 * - On iOS Safari: shows manual "Add to Home Screen" instructions
 * - Dismissed state expires after 7 days so users get another chance
 */
export default function InstallPrompt() {
  const { user } = useAuth();
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [showIOSPrompt, setShowIOSPrompt] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [guestDelayPassed, setGuestDelayPassed] = useState(false);

  useEffect(() => {
    // Don't show if already installed as PWA
    if (isInstalledPWA()) return;

    // Check for expired dismissal (7-day expiry instead of permanent)
    const dismissedAt = localStorage.getItem('pwa-install-dismissed-at');
    if (dismissedAt) {
      const daysSince = (Date.now() - parseInt(dismissedAt, 10)) / (1000 * 60 * 60 * 24);
      if (daysSince < 7) {
        setDismissed(true);
        return;
      }
      // Expired — clear and allow prompt again
      localStorage.removeItem('pwa-install-dismissed');
      localStorage.removeItem('pwa-install-dismissed-at');
    }

    // Android/Chrome: capture the beforeinstallprompt event
    const handleBeforeInstall = (e: Event) => {
      e.preventDefault();
      const evt = e as BeforeInstallPromptEvent;
      setDeferredPrompt(evt);
      globalDeferredPrompt = evt;
    };
    window.addEventListener('beforeinstallprompt', handleBeforeInstall);

    // iOS Safari detection — use improved detection that catches iPadOS
    if (isIOSSafari()) {
      const timer = setTimeout(() => setShowIOSPrompt(true), 30000);
      return () => {
        clearTimeout(timer);
        window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
      };
    }

    return () => window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
  }, []);

  // Listen for external "show iOS prompt" events (from footer Install button)
  useEffect(() => {
    const handleShowIOS = () => {
      setDismissed(false);
      setShowIOSPrompt(true);
    };
    window.addEventListener('pwa-show-ios-prompt', handleShowIOS);
    return () => window.removeEventListener('pwa-show-ios-prompt', handleShowIOS);
  }, []);

  // For non-logged-in users, wait 60s before showing the prompt
  useEffect(() => {
    if (user) return; // logged-in users see it immediately
    const timer = setTimeout(() => setGuestDelayPassed(true), 60000);
    return () => clearTimeout(timer);
  }, [user]);

  const handleInstallClick = useCallback(async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === 'accepted') {
      globalDeferredPrompt = null;
    }
    setDeferredPrompt(null);
    globalDeferredPrompt = null;
  }, [deferredPrompt]);

  const handleDismiss = () => {
    setDismissed(true);
    setDeferredPrompt(null);
    setShowIOSPrompt(false);
    localStorage.setItem('pwa-install-dismissed', 'true');
    localStorage.setItem('pwa-install-dismissed-at', String(Date.now()));
  };

  // Nothing to show
  if (dismissed) return null;
  if (!deferredPrompt && !showIOSPrompt) return null;
  // Logged-in users see it right away; guests see it after 60s
  if (!user && !guestDelayPassed) return null;

  return (
    <div className="fixed bottom-4 left-4 right-4 z-50 md:left-auto md:right-4 md:w-[360px] animate-fade-in">
      <div className="bg-ds-card border border-ds-border rounded-2xl shadow-2xl p-4 backdrop-blur-sm">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-ds-gold/[0.1] border border-ds-gold/20 flex items-center justify-center flex-shrink-0">
            <Smartphone className="w-5 h-5 text-ds-gold" />
          </div>

          <div className="flex-1 min-w-0">
            <p className="text-[14px] font-semibold text-ds-gray-1 mb-0.5">
              Get the Scholar Vision app
            </p>

            {deferredPrompt ? (
              <>
                <p className="text-[12px] text-ds-gray-2 leading-relaxed mb-3">
                  Add to your home screen for quick access to scholarships, deadlines, and your college matches.
                </p>
                <button
                  onClick={handleInstallClick}
                  className="inline-flex items-center gap-1.5 px-4 py-2 bg-ds-gold text-white text-[13px] font-semibold rounded-lg hover:bg-ds-gold/90 transition-colors"
                >
                  <Download className="w-3.5 h-3.5" />
                  Install App
                </button>
              </>
            ) : (
              <>
                <p className="text-[12px] text-ds-gray-2 leading-relaxed mb-2">
                  Tap the{' '}
                  <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 bg-ds-gold/[0.08] rounded text-ds-gold font-medium text-[11px]">
                    Share
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8" />
                      <polyline points="16 6 12 2 8 6" />
                      <line x1="12" y1="2" x2="12" y2="15" />
                    </svg>
                  </span>{' '}
                  button below, then <strong>Add to Home Screen</strong>.
                </p>
              </>
            )}
          </div>

          <button
            onClick={handleDismiss}
            className="p-1 rounded-lg hover:bg-ds-border/50 transition-colors flex-shrink-0"
            aria-label="Dismiss install prompt"
          >
            <X className="w-4 h-4 text-ds-gray-3" />
          </button>
        </div>
      </div>
    </div>
  );
}
